import { PaymentsCheckout_DlocalPaymentMethodType as DlocalPaymentMethod } from '__generated__/graphql-types';

import logger from 'js/app/loggerSingleton';
import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';

import CourseraPlusProductVariant from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import {
  NON_ACTIONABLE_CART_ERROR_CODES,
  localPaymentMethods,
  paymentMethods,
} from 'bundles/payments-common/common/constants';
import usePageData from 'bundles/payments-common/data/usePageData';
import type { DegreeInfo } from 'bundles/payments-common/hooks/useDegreeProgramInfo';
import { getPreEnrollmentIdFromQuery } from 'bundles/payments-common/lib/cartUtils';
import type { ServerErrorDetails } from 'bundles/payments-common/lib/paymentErrorUtils';
import { useLogGwgS12nUpsellCopyExposure } from 'bundles/payments-common/utils/gwgS12nUpsellExperimentUtils';
import { getTrackingData } from 'bundles/promotions/utils/productDiscountPromoUtils';

const PRODUCT_TYPE = {
  COURSE: 'course',
  SPECIALIZATION: 's12n',
  COURSERA_PLUS: 'cplus',
  COURSERA_LITE: 'clite',
  GUIDED_PROJECT: 'guided_project',
  PROJECT: 'project',
  CREDENTIAL_TRACK: 'master_track',
} as const;

const PAYMENT_PROCESSOR = {
  BRAINTREE: 'braintree',
  RAZORPAY: 'razorpay',
  STRIPE: 'stripe',
  STRIPEBV: 'stripe_bv',
  DLOCAL: 'dlocal',
} as const;

export const PAYMENT_METHOD = {
  BRAINTREE: 'braintree',
  RAZORPAY: 'razorpay',
  STRIPE_CONSUMER: 'stripe_consumer',
  PAYPAL: 'paypal',
  ZERO_DOLLAR: 'zero_dollar',
  MOCK_PAYMENT_PROCESSOR: 'mock_payment_processor',
  CARD: 'card',
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
  BANCONATCT: 'bancontact',
  IDEAL: 'ideal',
  LINK: 'link',
  SEPA_DEBIT: 'sepa_debit',
  KLARNA: 'klarna',
  US_BANK_ACCOUNT: 'us_bank_account',
  UPI: 'upi',
  PIX: 'pix',
  MERCADO_PAGO: 'mercado_pago',
  OVO: 'ovo',
  ID_LPM: 'id_lpm',
  GCASH: 'gcash',
  UNKNOWN: 'unknown',
} as const;

const CHECKOUT_VALIDATION_STATUS = {
  VALIDATION_SUCCESS: 'validation_success',
  VALIDATION_FAILED: 'validation_failed',
  TOKENIZATION_SUCCESS: 'tokenization_success',
  TOKENIZATION_FAILED: 'tokenization_failed',
} as const;

export const CHECKOUT_RESULT_STATUS = {
  PAID: 'paid',
  AWAITING_RESULT: 'awaiting_result',
  EXPIRED: 'expired',
  REQUIRES_ACTION: 'requires_action',
} as const;

export const getEventingV3ProductId = (
  cart: CartsV2,
  degreeProgram?: DegreeInfo,
  hasAcceptedCourseraPlusUpsell?: boolean
) => {
  if (hasAcceptedCourseraPlusUpsell) {
    return CourseraPlusProductVariant.MONTHLY_WITH_FREE_TRIAL;
  } else if (cart.isSpecialization || cart.isSpecializationPrepaid || cart.isSpecializationSubscription) {
    return cart.getSpecializationIdFromCartItem(cart.firstCartItem)!;
  } else if (degreeProgram && (cart.isCredentialTrackSubscription || cart.isCredentialTrack)) {
    return degreeProgram.id;
  } else {
    return cart.productItemId;
  }
};

export const getEventingV3ProductType = (cart: CartsV2, hasAcceptedCourseraPlusUpsell?: boolean) => {
  if (cart.isCourseraPlusSubscription || cart.isCourseraPlusPrepaid || hasAcceptedCourseraPlusUpsell) {
    return PRODUCT_TYPE.COURSERA_PLUS;
  } else if (cart.isCourseraLiteSubscription) {
    return PRODUCT_TYPE.COURSERA_LITE;
  } else if (cart.isForGuidedProject) {
    return PRODUCT_TYPE.GUIDED_PROJECT;
  } else if (cart.isForSelfPacedProject) {
    return PRODUCT_TYPE.PROJECT;
  } else if (cart.isVC) {
    return PRODUCT_TYPE.COURSE;
  } else if (cart.isSpecialization || cart.isSpecializationPrepaid || cart.isSpecializationSubscription) {
    return PRODUCT_TYPE.SPECIALIZATION;
  } else if (cart.isCredentialTrackSubscription || cart.isCredentialTrack) {
    return PRODUCT_TYPE.CREDENTIAL_TRACK;
  } else {
    logger.error(`Unable to get Eventing V3 productType for cart ${cart.id}`);
    return undefined;
  }
};

export const getEventingV3PaymentCart = (cart: CartsV2, preEnrollmentId?: string) => {
  return {
    id: preEnrollmentId ?? String(cart.id),
    creationDate: new Date(cart.cart.createdAt),
    amount: cart.totalAmount,
    currencyId: cart.currencyId.toString(),
    isPreEnroll: !!preEnrollmentId,
  };
};

export const getEventingV3TransactionProduct = (cart: CartsV2) => {
  return {
    id: cart.productItemId,
    type: cart.productType,
  };
};

export const getPaymentProcessor = (paymentMethod: string, isRouteToStripeBv?: boolean) => {
  if (
    paymentMethod === paymentMethods.stripeConsumer ||
    localPaymentMethods.includes(paymentMethod as keyof typeof paymentMethods) ||
    paymentMethod === paymentMethods.stripe_wallet
  ) {
    return isRouteToStripeBv ? PAYMENT_PROCESSOR.STRIPEBV : PAYMENT_PROCESSOR.STRIPE;
  } else if (paymentMethod === paymentMethods.razorpay) {
    return PAYMENT_PROCESSOR.RAZORPAY;
  } else if (paymentMethod === paymentMethods.dlocal) {
    return PAYMENT_PROCESSOR.DLOCAL;
  } else {
    return PAYMENT_PROCESSOR.BRAINTREE;
  }
};

const getPaymentMethodString = (paymentMethod: string, additionalPaymentMethod?: string) => {
  // dlocal payment methods is saved in dlocalCheckoutDetails?.selectedPaymentMethod
  if (paymentMethod === paymentMethods.dlocal && additionalPaymentMethod) {
    switch (additionalPaymentMethod) {
      case DlocalPaymentMethod.PixAutomatico:
        return PAYMENT_METHOD.PIX;
      case DlocalPaymentMethod.MercadoPagoOtp:
      case DlocalPaymentMethod.MercadoPagoRecurring:
        return PAYMENT_METHOD.MERCADO_PAGO;
      case DlocalPaymentMethod.Ovo:
        return PAYMENT_METHOD.OVO;
      case DlocalPaymentMethod.GcashOtp:
      case DlocalPaymentMethod.GcashRecurring:
        return PAYMENT_METHOD.GCASH;
      default:
        return PAYMENT_METHOD.UNKNOWN;
    }
  }

  if (paymentMethod === paymentMethods.razorpay && additionalPaymentMethod) {
    switch (additionalPaymentMethod) {
      case 'CARD':
        return PAYMENT_METHOD.CARD;
      case 'UPI':
        return PAYMENT_METHOD.UPI;
      default:
        return PAYMENT_METHOD.UNKNOWN;
    }
  }

  switch (paymentMethod) {
    case paymentMethods.paypal:
      return PAYMENT_METHOD.PAYPAL;
    case paymentMethods.stripeConsumer:
    case paymentMethods.braintree:
    case paymentMethods.card:
      return PAYMENT_METHOD.CARD;
    case paymentMethods.apple_pay:
      return PAYMENT_METHOD.APPLE_PAY;
    case paymentMethods.google_pay:
      return PAYMENT_METHOD.GOOGLE_PAY;
    case paymentMethods.bancontact:
      return PAYMENT_METHOD.BANCONATCT;
    case paymentMethods.ideal:
      return PAYMENT_METHOD.IDEAL;
    case paymentMethods.link:
      return PAYMENT_METHOD.LINK;
    case paymentMethods.sepa_debit:
      return PAYMENT_METHOD.SEPA_DEBIT;
    case paymentMethods.pix:
      return PAYMENT_METHOD.PIX;
    case paymentMethods.klarna:
      return PAYMENT_METHOD.KLARNA;
    case paymentMethods.razorpay:
    case paymentMethods.zeroDollar:
    case paymentMethods.mockPaymentProcessor:
    default:
      return PAYMENT_METHOD.UNKNOWN;
  }
};

const getPromotionFromCart = (cart: CartsV2) => {
  // Only !cart.hasPromotion matters, the rest are just to make the type checker happy
  if (!cart.hasPromotion || !cart.promotionId || !cart.firstCartItem?.promotionInfo?.promotionName) {
    return {};
  }
  const promoCode = cart.firstCartItem?.promotionInfo?.promoCode;
  return {
    promotion: {
      id: cart.promotionId?.toString(),
      name: cart.firstCartItem?.promotionInfo?.promotionName,
      ...(promoCode && { code: promoCode }),
    },
  };
};

export const usePaymentEventing = () => {
  const { cart, degreeProgram } = usePageData();
  const retrackedTrack = useRetracked();
  const track = useTracker();
  const { query } = useLocation();
  const preEnrollmentId = getPreEnrollmentIdFromQuery(query);
  const logGwgS12nUpsellCopyExposure = useLogGwgS12nUpsellCopyExposure(cart);

  const trackCheckoutPageView = () => {
    track('view_checkout_page', {
      paymentCart: getEventingV3PaymentCart(cart, preEnrollmentId),
      transactionProduct: getEventingV3TransactionProduct(cart),
      product: {
        id: getEventingV3ProductId(cart, degreeProgram),
        type: getEventingV3ProductType(cart),
      },
      ...getPromotionFromCart(cart),
    });
  };

  const trackCheckoutResultView = ({
    submissionErrorCode,
    paymentCheckoutStatus,
    submissionErrorServerDetails,
  }: {
    submissionErrorCode?: string;
    paymentCheckoutStatus?: (typeof CHECKOUT_RESULT_STATUS)[keyof typeof CHECKOUT_RESULT_STATUS];
    submissionErrorServerDetails?: ServerErrorDetails;
  }) => {
    track('view_checkout_result', {
      paymentCart: getEventingV3PaymentCart(cart, preEnrollmentId),
      product: {
        id: getEventingV3ProductId(cart, degreeProgram),
        type: getEventingV3ProductType(cart),
      },
      transactionProduct: getEventingV3TransactionProduct(cart),
      ...getPromotionFromCart(cart),
      paymentCheckoutStatus:
        paymentCheckoutStatus ??
        ([...NON_ACTIONABLE_CART_ERROR_CODES, 'checkout-intent-expired', 'checkout-intent-reinitialize'].includes(
          submissionErrorCode ?? ''
        )
          ? CHECKOUT_RESULT_STATUS.EXPIRED
          : CHECKOUT_RESULT_STATUS.REQUIRES_ACTION),
      ...(submissionErrorCode && {
        errorMessage: {
          id: submissionErrorCode,
          description: submissionErrorServerDetails?.processorResponseText ?? submissionErrorCode,
        },
      }),
    });
  };

  const trackPaymentButtonClick = ({
    isUsingSavedWallet,
    isLoggedInToPaypal,
    isCreditCardFormValid,
    isRealCheckoutAttempt,
    paymentMethod,
    isRouteToStripeBv,
    isSandboxEnabled,
    hasSucceededPaymentMethod,
    additionalPaymentMethod,
  }: {
    isUsingSavedWallet?: boolean;
    isLoggedInToPaypal?: boolean;
    isCreditCardFormValid?: boolean;
    isRealCheckoutAttempt?: boolean;
    shouldStoreCreditCard?: boolean;
    paymentMethod?: string;
    isRouteToStripeBv?: boolean;
    isSandboxEnabled?: boolean;
    hasSucceededPaymentMethod?: boolean;
    additionalPaymentMethod?: string;
  }) => {
    const promoTrackingData = getTrackingData();
    const paymentProcessor = paymentMethod && getPaymentProcessor(paymentMethod, isRouteToStripeBv);

    let checkoutValidationStatus: (typeof CHECKOUT_VALIDATION_STATUS)[keyof typeof CHECKOUT_VALIDATION_STATUS] =
      isRealCheckoutAttempt
        ? CHECKOUT_VALIDATION_STATUS.VALIDATION_SUCCESS
        : CHECKOUT_VALIDATION_STATUS.VALIDATION_FAILED;
    if (hasSucceededPaymentMethod !== undefined) {
      checkoutValidationStatus = hasSucceededPaymentMethod
        ? CHECKOUT_VALIDATION_STATUS.TOKENIZATION_SUCCESS
        : CHECKOUT_VALIDATION_STATUS.TOKENIZATION_FAILED;
    }

    const trackingV2Data = {
      cartId: cart.id,
      ...promoTrackingData,
      isUsingSavedWallet,
      isLoggedInToPaypal,
      isCreditCardFormValid,
      isRealCheckoutAttempt,
      ...(paymentMethod ? { paymentMethod: getPaymentMethodString(paymentMethod, additionalPaymentMethod) } : {}),
      paymentProcessor,
      isSandbox: isSandboxEnabled,
    };

    retrackedTrack({
      trackingData: trackingV2Data,
      trackingName: 'pay_now',
      action: 'click',
    });

    if (!paymentMethod || !paymentProcessor) {
      return;
    }

    const trackingV3Data = {
      paymentCart: getEventingV3PaymentCart(cart, preEnrollmentId),
      product: {
        id: getEventingV3ProductId(cart, degreeProgram),
        type: getEventingV3ProductType(cart),
      },
      transactionProduct: getEventingV3TransactionProduct(cart),
      paymentMethod: { type: getPaymentMethodString(paymentMethod, additionalPaymentMethod) },
      paymentProcessor: { id: paymentProcessor },
      isRealCheckoutAttempt: !!isRealCheckoutAttempt,
      ...getPromotionFromCart(cart),
      isSandbox: isSandboxEnabled,
      isUsingSavedPaymentMethod: isUsingSavedWallet,
      checkoutValidationStatus,
    };

    track('click_pay_now_button', trackingV3Data);

    // Impression point for the GwG S12N post-purchase upsell copy experiment (MONETIZE-2793): the
    // pay-now / "Start free trial" click.
    logGwgS12nUpsellCopyExposure();
  };

  return {
    trackCheckoutPageView,
    trackCheckoutResultView,
    trackPaymentButtonClick,
  };
};
